/* ==========================================================================
   /pages/subscribe-v3, shared page behaviour for every `subscribe-v3-*` section.

   Why this is one asset instead of per-section inline script:
   sections/subscribe.liquid and assets/subscribe-v2.js each carry their own
   copy of the add-to-cart + `_ph_distinct_id` join-key logic, and CLAUDE.md
   flags that the arms MUST stay symmetric or server-side conversion joins
   bias one arm. v3 is split across several sections, so N inline copies would
   be N chances to drift. There is exactly one implementation here, bound at
   the document level, and it is idempotent so section re-renders in the theme
   editor never double-bind.

   Behaviour is a faithful port of the inline script in sections/subscribe.liquid:
   scroll reveal, single-open FAQ accordion, add-to-cart CTAs that POST to
   /cart/add.js then redirect, and smooth in-page anchor scroll.
   ========================================================================== */
(function () {
  'use strict';

  // Every v3 section emits this <script src>, and identical tags each execute.
  // Without this guard the delegated click handler would bind N times and a
  // single CTA click would POST /cart/add.js N times.
  if (window.__sxsV3Loaded) return;
  window.__sxsV3Loaded = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----- Scroll reveal ---------------------------------------------------- */
  var io = null;
  if (!reduce && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('sxs-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
  }

  function bindReveals(root) {
    root.querySelectorAll('.sxs-reveal').forEach(function (el) {
      if (el.dataset.sxsRevealBound) return;
      el.dataset.sxsRevealBound = '1';
      if (io) io.observe(el);
      else el.classList.add('sxs-in');
    });
  }

  /* ----- FAQ accordion (single-open, keyboard-accessible) ----------------- */
  function setOpen(item, open) {
    var btn = item.querySelector('.sxs-faq__q');
    var ans = item.querySelector('.sxs-faq__a');
    item.classList.toggle('sxs-open', open);
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (ans) ans.style.maxHeight = open ? ans.scrollHeight + 'px' : '0px';
  }

  function bindFaq(root) {
    root.querySelectorAll('.sxs-faq').forEach(function (group) {
      if (group.dataset.sxsFaqBound) return;
      group.dataset.sxsFaqBound = '1';
      var items = group.querySelectorAll('.sxs-faq__item');
      items.forEach(function (item) {
        if (item.classList.contains('sxs-open')) setOpen(item, true);
        var btn = item.querySelector('.sxs-faq__q');
        if (!btn) return;
        btn.addEventListener('click', function () {
          var willOpen = !item.classList.contains('sxs-open');
          items.forEach(function (other) { if (other !== item) setOpen(other, false); });
          setOpen(item, willOpen);
        });
      });
    });
  }

  // Recompute the open answer's height on resize so wrapped text isn't clipped.
  window.addEventListener('resize', function () {
    document.querySelectorAll('.sxs-faq__item.sxs-open').forEach(function (open) {
      var a = open.querySelector('.sxs-faq__a');
      if (a) a.style.maxHeight = a.scrollHeight + 'px';
    });
  });

  /* ----- PostHog join key -------------------------------------------------
     Same contract as sections/subscribe.liquid and assets/subscribe-v2.js:
     this page bounces straight to /checkout after /cart/add.js, beating the
     sbs-ph-cart-id snippet's delayed attribute write, so the distinct id is
     written here. If posthog-js is blocked none of this runs and behaviour is
     unchanged. Tracking must never delay or break navigation, hence the caps.
     ---------------------------------------------------------------------- */
  function phId() {
    try {
      var p = window.posthog;
      if (p && p.__loaded && typeof p.get_distinct_id === 'function') return p.get_distinct_id() || null;
    } catch (e) { /* ignore */ }
    return null;
  }

  function patchPhHrefs() {
    var id = phId();
    if (!id) return false;
    document.querySelectorAll('[data-sxs-add]').forEach(function (el) {
      var href = el.getAttribute('href');
      if (!href || href.indexOf('/cart/') === -1 || href.indexOf('_ph_distinct_id') !== -1) return;
      el.setAttribute('href', href + (href.indexOf('?') === -1 ? '?' : '&') +
        'attributes%5B_ph_distinct_id%5D=' + encodeURIComponent(id));
    });
    return true;
  }

  if (!patchPhHrefs()) {
    var tries = 0;
    var poll = setInterval(function () {
      if (patchPhHrefs() || ++tries >= 40) clearInterval(poll);
    }, 250);
  }

  function writePhJoinKey() {
    var id = phId();
    if (!id) return Promise.resolve();
    var o = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _ph_distinct_id: id } })
    };
    if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) o.signal = AbortSignal.timeout(900);
    return Promise.race([
      fetch('/cart/update.js', o).catch(function () { /* best-effort */ }),
      new Promise(function (res) { setTimeout(res, 900); })
    ]);
  }

  /* ----- Add-to-cart CTAs --------------------------------------------------
     Delegated from the document so sections rendered later (theme editor,
     experiment arms) are covered without rebinding. Deliberately not a <form>:
     page-builder apps hijack the first add-to-cart form/submit on a page.
     ---------------------------------------------------------------------- */
  /* window.fetch here is re-wrapped by several apps (Appstle wraps it 3x plus
     XHR, shop.app, the attribution bridge). A buggy wrapper can strand the
     add promise — never resolves, rejects, or honours the abort signal — which
     left subscribe-v2's button busy forever (rage-click incident, 2026-07-16).
     The failsafe timer below is independent of the promise: after 5s we bail
     to the cart-permalink href (already carrying the _ph_distinct_id attribute
     via patchPhHrefs), a server-side add no wrapper can break. */
  function unbusy(el) {
    delete el.dataset.sxsBusy;
    el.classList.remove('sxs-btn--busy');
    if (el.__sxsLabel) el.innerHTML = el.__sxsLabel;
  }
  /* Back/forward-cache restore brings the page back mid-add with the button
     still busy — re-arm every CTA. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('[data-sxs-add]').forEach(unbusy);
  });

  document.addEventListener('click', function (ev) {
    var el = ev.target.closest ? ev.target.closest('[data-sxs-add]') : null;
    if (!el) return;
    var id = el.getAttribute('data-variant-id');
    if (!id) return; // nothing to add; let the href fallback run

    ev.preventDefault();
    ev.stopPropagation();
    if (el.dataset.sxsBusy) return; // double-submit guard; failsafe re-arms us
    if (!el.__sxsLabel) el.__sxsLabel = el.innerHTML;
    el.dataset.sxsBusy = '1';
    el.classList.add('sxs-btn--busy');
    el.textContent = 'Adding…'; // visible feedback so a wait never reads as a dead button

    var ret = el.getAttribute('data-return-to') || '/checkout';
    var data = 'id=' + encodeURIComponent(id) + '&quantity=1';
    var plan = el.getAttribute('data-selling-plan');
    if (plan) data += '&selling_plan=' + encodeURIComponent(plan);
    /* Optional line-item properties, JSON in data-properties (subscribe-v5
       sends the Square's name). Malformed JSON is ignored, never fatal. */
    var propsRaw = el.getAttribute('data-properties');
    if (propsRaw) {
      try {
        var props = JSON.parse(propsRaw);
        Object.keys(props).forEach(function (k) {
          if (props[k] === '' || props[k] == null) return;
          data += '&properties[' + encodeURIComponent(k) + ']=' + encodeURIComponent(props[k]);
        });
      } catch (e) { /* ignore */ }
    }

    var settled = false;
    var settle = function (nav) {
      if (settled) return;
      settled = true;
      clearTimeout(failsafe);
      if (nav) window.location.href = nav;
      else unbusy(el);
    };
    var failsafe = setTimeout(function () {
      settle(el.getAttribute('href') || ret);
    }, 5000);

    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: data
    };
    if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) opts.signal = AbortSignal.timeout(12000);
    fetch('/cart/add.js', opts).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return writePhJoinKey().then(function () { settle(ret); });
    }).catch(function () {
      settle(el.getAttribute('href'));
    });
  });

  /* ----- Smooth in-page anchor scroll -------------------------------------
     Experiment arms are stacked on one page and reuse anchor ids (#sxs-offer
     exists in the control arm AND the v3 arm), so getElementById can return a
     HIDDEN arm's element and the scroll silently no-ops. Prefer the visible
     match (offsetParent is null inside any display:none ancestor). */
  document.addEventListener('click', function (ev) {
    var a = ev.target.closest ? ev.target.closest('a[href^="#sxs-"]') : null;
    if (!a || a.hasAttribute('data-sxs-add')) return;
    var idv = a.getAttribute('href').slice(1);
    var matches = document.querySelectorAll('[id="' + idv + '"]');
    if (!matches.length) return;
    var target = null;
    for (var i = 0; i < matches.length; i++) {
      if (matches[i].offsetParent !== null) { target = matches[i]; break; }
    }
    if (!target) target = matches[0];
    ev.preventDefault();
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  });

  /* ----- Leaf confetti (Emely, subscribe-v5) --------------------------------
     Leaf-ish flecks on a gravity arc, fired from the selector's button on
     Protect. It only decorates: the add-to-cart handler above does the work,
     and the burst starts before /cart/add.js resolves so it shows during the
     half-second before the checkout redirect. Skipped under reduced motion.
     ---------------------------------------------------------------------- */
  var CONFETTI_COLORS = ['#1f4021', '#2c5430', '#3e6e3f', '#6aa06a', '#8cdc69', '#a6e989', '#c9f3b3', '#e4f8d8', '#f0edea'];
  /* One closure per piece. `var` in the loop below is function-scoped, so
     handlers written inline there would all see the LAST piece and remove it
     140 times while the rest stayed in the DOM at opacity 0 (fill: forwards);
     the original mock had exactly that. */
  function launch(piece, frames, seconds) {
    piece.animate(frames, { duration: seconds * 1000, easing: 'linear', fill: 'forwards' }).onfinish = function () { piece.remove(); };
    // onfinish never fires while a tab is hidden (animations pause), so a
    // plain timer removes the piece regardless.
    setTimeout(function () { piece.remove(); }, seconds * 1000 + 300);
  }
  function confetti(el, big) {
    if (!el || reduce || !el.animate) return;
    var r = el.getBoundingClientRect(), n = big ? 140 : 26;
    for (var i = 0; i < n; i++) {
      var d = document.createElement('i'), kind = Math.random();
      d.className = 'sxs-confetti' + (kind < 0.45 ? ' sxs-confetti--leaf' : kind < 0.7 ? ' sxs-confetti--dot' : '');
      var size = big ? 4 + Math.random() * 7 : 3 + Math.random() * 5;
      d.style.width = size + 'px';
      d.style.height = (kind < 0.45 ? size * 1.7 : kind < 0.7 ? size : size * 0.6) + 'px';
      d.style.background = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
      var x0 = big ? r.left + Math.random() * r.width : r.left + r.width / 2, y0 = r.top + r.height / 2;
      d.style.left = x0 + 'px';
      d.style.top = y0 + 'px';
      document.body.appendChild(d);
      var ang = (-Math.PI / 2) + (Math.random() - 0.5) * (big ? Math.PI * 1.1 : Math.PI * 1.4);
      var v = big ? 180 + Math.random() * 260 : 70 + Math.random() * 90;
      var vx = Math.cos(ang) * v, vy = Math.sin(ang) * v, g = big ? 520 : 380, drift = (Math.random() - 0.5) * (big ? 80 : 40);
      var T = (big ? 1.6 : 1.0) + Math.random() * 0.6, steps = 14, frames = [], spin = (Math.random() < 0.5 ? -1 : 1) * 540;
      for (var k = 0; k <= steps; k++) {
        var tt = T * k / steps, x = vx * tt + drift * tt * tt, y = vy * tt + 0.5 * g * tt * tt;
        frames.push({
          transform: 'translate(calc(-50% + ' + x.toFixed(1) + 'px),calc(-50% + ' + y.toFixed(1) + 'px)) rotate(' + (tt * spin).toFixed(0) + 'deg) scale(' + (1 - k / steps * 0.3).toFixed(2) + ')',
          opacity: k < steps * 0.65 ? 1 : 1 - (k - steps * 0.65) / (steps * 0.35)
        });
      }
      launch(d, frames, T);
    }
  }

  /* ----- Tier selector (sections/subscribe-v3-tiles.liquid) ----------------
     Picking a tile or chip rewrites the confirm line, the price and the CTA
     in place. The CTA keeps the data-sxs-add contract above: only its variant,
     selling plan and label change, so the path to checkout stays the shared
     one and buy_cta_clicked (layout/theme.liquid) reads the live selection.
     ---------------------------------------------------------------------- */
  function bindTiles(root) {
    root.querySelectorAll('[data-sxs-tiles]').forEach(function (box) {
      if (box.dataset.sxsTilesBound) return;
      box.dataset.sxsTilesBound = '1';
      var tiers = box.querySelectorAll('[data-sxs-tier]');
      var line = box.querySelector('[data-sxs-line]');
      var pricing = box.querySelector('[data-sxs-pricing]');
      var cta = box.querySelector('[data-sxs-add]');
      // The big burst on Protect. Fires before the document-level add handler
      // marks the button busy, so a second click on a busy button stays quiet.
      if (cta) {
        cta.addEventListener('click', function () {
          if (!cta.dataset.sxsBusy && cta.getAttribute('data-variant-id')) confetti(cta, true);
        });
      }
      var icon = cta ? cta.querySelector('svg') : null;
      var iconHtml = icon ? icon.outerHTML : '';
      var tpl = {
        line: box.getAttribute('data-line-template') || '',
        pricing: box.getAttribute('data-pricing-template') || '',
        cta: box.getAttribute('data-cta-template') || ''
      };

      function fill(t, d) {
        return t.replace(/\{(\w+)\}/g, function (m, k) { return d[k] != null ? d[k] : m; });
      }

      function apply(el, byUser) {
        tiers.forEach(function (o) { o.classList.toggle('sxs-on', o === el); });
        var n = parseInt(el.getAttribute('data-sqm'), 10) || 0;
        var d = {
          name: el.getAttribute('data-name') || '',
          sqm: n,
          squares: n === 1 ? '1 Square' : n + ' Squares',
          year: (n * 12).toLocaleString('en-AU'),
          price: el.getAttribute('data-price') || ''
        };
        if (line && tpl.line) line.innerHTML = fill(tpl.line, d);
        if (pricing && tpl.pricing) pricing.textContent = fill(tpl.pricing, d);
        if (cta) {
          var variant = el.getAttribute('data-variant');
          var plan = el.getAttribute('data-plan');
          if (variant) cta.setAttribute('data-variant-id', variant);
          if (plan) cta.setAttribute('data-selling-plan', plan);
          else cta.removeAttribute('data-selling-plan');
          if (tpl.cta) {
            cta.innerHTML = iconHtml + fill(tpl.cta, d);
            cta.__sxsLabel = cta.innerHTML; // what unbusy() restores after an add
          }
        }
        if (byUser && window.posthog && posthog.capture) {
          try {
            posthog.capture('subscription_tier_selected', {
              name: d.name, sqm: n, price: d.price, page_path: window.location.pathname
            });
          } catch (e) { /* never block the selection */ }
        }
      }

      function current() {
        for (var i = 0; i < tiers.length; i++) {
          var inp = tiers[i].querySelector('input[type="radio"]');
          if (inp && inp.checked) return tiers[i];
        }
        return null;
      }

      tiers.forEach(function (o) {
        var input = o.querySelector('input[type="radio"]');
        if (!input) return;
        input.addEventListener('change', function () { if (input.checked) apply(o, true); });
        input.addEventListener('focus', function () { o.classList.add('sxs-focus'); });
        input.addEventListener('blur', function () { o.classList.remove('sxs-focus'); });
      });
      var first = current();
      if (first) apply(first, false);

      // Back/forward navigation (bfcache or a history load) restores the radio
      // state after this ran and fires no change event, so the line, price
      // and CTA would describe one tier while the ring showed another
      // (seen 16 Sep 2026 after checkout → back). Re-sync from the radios.
      window.addEventListener('pageshow', function () {
        var el = current();
        if (el) apply(el, false);
      });
    });
  }

  /* ----- Boot ------------------------------------------------------------- */
  function init(root) {
    bindReveals(root);
    bindFaq(root);
    bindTiles(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor: re-scan a section after it is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
